import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import Draggable from "react-draggable";
import { setcqlData } from "../../../features/layers/cqlDataSlice";
import { selectLayerDataSet } from "../../../features/layers/overlaylayerslice";
import { jsPDF } from "jspdf";
import { FaFilePdf, FaTimes } from "react-icons/fa";
import { useMap } from "react-leaflet";

const VerticalProfile = ({ position = {}, location, onClose }) => {
  const dispatch = useDispatch();
  const map = useMap();
  const [loading, setLoading] = useState(true);
  const [locationName, setLocationName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const exportRef = useRef(null);
  const data = useSelector(selectLayerDataSet);
  const [odates, setoDates] = useState();
  const [tdates, setTDates] = useState();
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Function to subtract one day from a date string
  const subtractOneDay = (dateString) => {
    if (!dateString) return null;
    
    try {
      // Parse the date string (assuming format: "2025-09-28-hr-00-00-00")
      const [datePart, timePart] = dateString.split('-hr-');
      const [year, month, day] = datePart.split('-').map(Number);
      
      // Create a Date object and subtract one day
      const date = new Date(year, month - 1, day);
      date.setDate(date.getDate() - 1);
      
      // Format back to the original format
      const newYear = date.getFullYear();
      const newMonth = String(date.getMonth() + 1).padStart(2, '0');
      const newDay = String(date.getDate()).padStart(2, '0');
      
      return `${newYear}-${newMonth}-${newDay}-hr-${timePart}`;
    } catch (error) {
      console.error("Error subtracting one day from date:", error);
      return dateString; // Return original if error
    }
  };

  // Track window dimensions for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Disable map scroll when modal is open
  useEffect(() => {
    if (map) {
      map.scrollWheelZoom.disable();
    }

    return () => {
      if (map) {
        map.scrollWheelZoom.enable();
      }
    };
  }, [map]);

  // Extract odate from overlay layers data
  useEffect(() => {
    const extractODate = (data) => {
      if (data.show && data.class === 'Model') {
        // Use the date directly from the overlay layer data
        // Format should be like "2025-09-28-hr-00-00-00"
        if (data.date) {
          setoDates(data.date);
          
          // Extract date part for display (YYYY-MM-DD)
          const datePart = data.date.split('-hr-')[0];
          const formattedDate = new Date(datePart).toLocaleDateString('en-GB', {
            weekday: 'short',
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          });
          setTDates(formattedDate);
        }
      }
    };

    if (Array.isArray(data)) {
      data.forEach((overlayer) => extractODate(overlayer));
    }
  }, [data]);

  // Fetch Vertical Profile data
  const getVerticalProfileData = async (lat, lng) => {
    setLoading(true);
    setError(null);
    try {
      // Subtract one day from the odate for the API request
      const adjustedOdate = subtractOneDay(odates);
      
      if (!adjustedOdate) {
        throw new Error("Invalid date format");
      }

      // Use the vertical profile API endpoint with coordinates and adjusted odate (one day back)
      const response = await axios.get(
        `https://api.ncmrwf.gov.in/vprofile/?coords=${lat},${lng}&odate=${adjustedOdate}`,
        {
          responseType: 'blob',
          timeout: 25000
        }
      );

      if (!response.data) {
        throw new Error("No image data received");
      }

      const imageUrl = URL.createObjectURL(response.data);
      setImageUrl(imageUrl);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error.message || "Failed to load Vertical Profile");
      console.error(error);
    }
  };

  // Handle PDF download
  const handleDownload = async () => {
    if (!imageUrl) return;

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      pdf.setFontSize(12);
      pdf.setTextColor(51, 51, 51);
      pdf.text('National Centre for Medium Range Weather Forecasting (NCMWRF)', 105, 10, { align: 'center' });

      pdf.setFontSize(16);
      pdf.text('Vertical Profile', 105, 20, { align: 'center' });

      pdf.setFontSize(12);
      pdf.text(locationName || '', 105, 27, { align: 'center' });
      
      // Extract hour from odate for display
      const hour = odates ? odates.split('-hr-')[1]?.split('-')[0] || '00' : '00';
      
      // For display, also show the adjusted date (one day back)
      const adjustedDate = subtractOneDay(odates);
      const displayDatePart = adjustedDate ? adjustedDate.split('-hr-')[0] : '';
      const formattedDisplayDate = displayDatePart ? new Date(displayDatePart).toLocaleDateString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }) : tdates;
      
      pdf.text(`Vertical Profile ${formattedDisplayDate} ${hour} UTC`, 105, 33, { align: 'center' });

      const img = new Image();
      img.src = imageUrl;

      await new Promise((resolve) => {
        if (img.complete) resolve();
        else {
          img.onload = resolve;
          img.onerror = resolve;
        }
      });

      const pageWidth = pdf.internal.pageSize.getWidth() - 20;
      const pageHeight = pdf.internal.pageSize.getHeight() - 45;

      let imgWidth = pageWidth;
      let imgHeight = (img.height * pageWidth) / img.width;

      if (imgHeight > pageHeight) {
        imgHeight = pageHeight;
        imgWidth = (img.width * pageHeight) / img.height;
      }

      const xPos = (pdf.internal.pageSize.getWidth() - imgWidth) / 2;

      pdf.addImage(img.src, 'PNG', xPos, 35, imgWidth, imgHeight);
      pdf.save(`VerticalProfile_NCMRWF_${locationName ? locationName.replace(/[^a-z0-9]/gi, '_') : 'location'}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
    }
  };

  // Close the modal
  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    dispatch(setcqlData({ show: false }));
    onClose();
  };

  // Prevent event propagation
  const handleContainerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Get location name from coordinates
  const getLocationName = async (lat, lng) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        { timeout: 5000 }
      );
      if (response.data.display_name) {
        setLocationName(response.data.display_name);
      }
    } catch (error) {
      console.error("Error fetching location name:", error);
    }
  };

  // Handle outside clicks
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        handleClose(e);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  // Fetch data when coordinates or odate changes
  useEffect(() => {
    if (odates) {
      if (position?.location?.lat && position?.location?.lng) {
        const { lat, lng } = position.location;
        getVerticalProfileData(lat, lng);
        getLocationName(lat, lng);
      } else if (location?.lat && location?.lng) {
        const { lat, lng } = location;
        getVerticalProfileData(lat, lng);
        getLocationName(lat, lng);
      } else {
        setError("Location data is missing");
        setLoading(false);
      }
    } else {
      setError("Date data is missing");
      setLoading(false);
    }

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [position, location, odates]);

  // Extract hour from odate for display
  const getDisplayHour = () => {
    if (!odates) return '00';
    const hour = odates.split('-hr-')[1]?.split('-')[0];
    return hour || '00';
  };

  // Get display date (one day back for the API)
  const getDisplayDate = () => {
    if (!odates) return tdates || '';
    
    const adjustedDate = subtractOneDay(odates);
    if (!adjustedDate) return tdates || '';
    
    const datePart = adjustedDate.split('-hr-')[0];
    return new Date(datePart).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <>
      {loading && !error && (
        <LoaderModal>
          <div className="modal-content">
            <i className="fa fa-cog fa-spin" style={{ marginRight: "10px" }}></i>
            Loading Vertical Profile...
          </div>
        </LoaderModal>
      )}

      {error && (
        <ErrorModal>
          <div className="modal-content">
            <i className="fa fa-exclamation-triangle" style={{ marginRight: "10px", color: "#ff3333" }}></i>
            {error}
            <div className="button-group">
              <button
                onClick={() => {
                  setError(null);
                  const { lat, lng } = position?.location || location || {};
                  if (lat && lng && odates) getVerticalProfileData(lat, lng);
                }}
                className="retry-button"
              >
                Retry
              </button>
              <button onClick={handleClose} className="close-button">
                Close
              </button>
            </div>
          </div>
        </ErrorModal>
      )}

      {!loading && !error && (
        <Draggable 
          handle=".headers" 
          bounds="parent"
          disabled={dimensions.width <= 768} // Disable dragging on mobile
        >
          <DiagramContainer
            ref={containerRef}
            onClick={handleContainerClick}
            onMouseDown={handleContainerClick}
            dimensions={dimensions}
          >
            <div className="headers">
              <div className="header-content">
                <div className="header-text">
                  <p className="title">Vertical Profile</p>
                  <p className="location">{locationName}</p>
                  <p className="date-info">
                    Vertical Profile {getDisplayDate()} {getDisplayHour()} UTC
                  </p>
                </div>
                <div className="header-actions">
                  <button 
                    className="download-btn" 
                    onClick={handleDownload} 
                    title="Download as PDF"
                    aria-label="Download as PDF"
                  >
                    <FaFilePdf className="pdf-icon" />
                  </button>
                  <button 
                    className="close" 
                    onClick={handleClose} 
                    title="Close"
                    aria-label="Close diagram"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            </div>

            <div
              className="image-scroll-container"
              ref={exportRef}
              onWheel={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <div className="image-container">
                {imageUrl && <img src={imageUrl} alt="Vertical Profile" />}
              </div>
            </div>
          </DiagramContainer>
        </Draggable>
      )}
    </>
  );
};

export default VerticalProfile;

// Styled components remain the same
const DiagramContainer = styled.div`
  position: absolute;
  background: #fff;
  z-index: 99999;
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0,0,0,0.3);
  overflow: hidden;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  
  /* Responsive sizing based on viewport width */
  ${({ dimensions }) => {
    if (dimensions.width > 1200) {
      return `
        width: 600px;
        height: 550px;
        left: calc(50% - 300px);
        top: 50px;
      `;
    } else if (dimensions.width > 992) {
      return `
        width: 550px;
        height: 500px;
        left: calc(50% - 275px);
        top: 40px;
      `;
    } else if (dimensions.width > 768) {
      return `
        width: 500px;
        height: 450px;
        left: calc(50% - 250px);
        top: 30px;
      `;
    } else if (dimensions.width > 576) {
      return `
        width: 90vw;
        height: 80vh;
        left: 5vw;
        top: 10vh;
      `;
    } else {
      return `
        width: 95vw;
        height: 85vh;
        left: 2.5vw;
        top: 7.5vh;
      `;
    }
  }}

  .headers {
    padding: ${({ dimensions }) => dimensions.width > 768 ? '12px 15px' : '10px 12px'};
    cursor: ${({ dimensions }) => dimensions.width > 768 ? 'move' : 'default'};
    border-bottom: 1px solid #eee;
    user-select: none;
    background: #f8f9fa;
    
    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 10px;
    }
    
    .header-text {
      flex: 1;
      min-width: 0;
      overflow: hidden;
    }
    
    .title {
      font-weight: bold;
      color: #333;
      margin: 0;
      font-size: ${({ dimensions }) => 
        dimensions.width > 1200 ? '18px' : 
        dimensions.width > 768 ? '17px' : '16px'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .location {
      color: #666;
      margin: ${({ dimensions }) => dimensions.width > 768 ? '4px 0 0 0' : '3px 0 0 0'};
      font-size: ${({ dimensions }) => 
        dimensions.width > 1200 ? '14px' : 
        dimensions.width > 768 ? '13px' : '12px'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .date-info {
      color: #666;
      margin: ${({ dimensions }) => dimensions.width > 768 ? '4px 0 0 0' : '3px 0 0 0'};
      font-size: ${({ dimensions }) => 
        dimensions.width > 1200 ? '12px' : 
        dimensions.width > 768 ? '11px' : '10px'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  
  .header-actions {
    display: flex;
    align-items: center;
    gap: ${({ dimensions }) => dimensions.width > 768 ? '10px' : '8px'};
    flex-shrink: 0;
  }
  
  .download-btn {
    background: #3f4854;
    color: white;
    border: none;
    width: ${({ dimensions }) => 
      dimensions.width > 1200 ? '32px' : 
      dimensions.width > 768 ? '30px' : '28px'};
    height: ${({ dimensions }) => 
      dimensions.width > 1200 ? '32px' : 
      dimensions.width > 768 ? '30px' : '28px'};
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.2s ease;
    
    &:hover {
      background: #2c343d;
      transform: scale(1.05);
    }
    
    &:active {
      transform: scale(0.95);
    }
    
    .pdf-icon {
      font-size: ${({ dimensions }) => 
        dimensions.width > 1200 ? '16px' : 
        dimensions.width > 768 ? '15px' : '14px'};
    }
  }
  
  .close {
    cursor: pointer;
    color: #666;
    background: none;
    border: none;
    padding: 0;
    width: ${({ dimensions }) => 
      dimensions.width > 1200 ? '32px' : 
      dimensions.width > 768 ? '30px' : '28px'};
    height: ${({ dimensions }) => 
      dimensions.width > 1200 ? '32px' : 
      dimensions.width > 768 ? '30px' : '28px'};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
      color: #333;
      transform: scale(1.05);
    }
    
    &:active {
      transform: scale(0.95);
    }
    
    svg {
      font-size: ${({ dimensions }) => 
        dimensions.width > 1200 ? '18px' : 
        dimensions.width > 768 ? '17px' : '16px'};
    }
  }
  
  .image-scroll-container {
    height: calc(100% - ${({ dimensions }) => dimensions.width > 768 ? '70px' : '65px'});
    overflow-y: auto;
    padding: ${({ dimensions }) => dimensions.width > 768 ? '12px' : '8px'};
    background: #f5f5f5;
    
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }
    
    .image-container {
      width: 100%;
      background: white;
      border-radius: 4px;
      overflow: hidden;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      
      img {
        width: 100%;
        height: auto;
        display: block;
      }
    }
  }
`;

const LoaderModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000;
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);

  .modal-content {
    background: white;
    padding: clamp(15px, 5vw, 25px) clamp(20px, 6vw, 30px);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    font-size: clamp(15px, 4vw, 18px);
    font-weight: 500;
    color: #3f4854;
    display: flex;
    align-items: center;
    max-width: 90%;
    width: auto;
    min-width: 200px;
    text-align: center;
    border: 1px solid rgba(0, 0, 0, 0.1);

    @media (max-width: 480px) {
      flex-direction: column;
      gap: 12px;
    }

    .fa-cog {
      font-size: clamp(18px, 5vw, 22px);
      margin-right: clamp(8px, 2vw, 12px);

      @media (max-width: 480px) {
        margin-right: 0;
        margin-bottom: 8px;
      }
    }
  }
`;

const ErrorModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000;
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);

  .modal-content {
    background: white;
    padding: clamp(15px, 5vw, 25px) clamp(15px, 5vw, 30px);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    font-size: clamp(15px, 4vw, 18px);
    font-weight: 500;
    color: #3f4854;
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 90%;
    width: auto;
    min-width: 200px;
    text-align: center;
    border: 1px solid rgba(0, 0, 0, 0.1);

    .fa-exclamation-triangle {
      font-size: clamp(20px, 6vw, 24px);
      margin-bottom: clamp(8px, 2vw, 12px);
    }
  }

  .button-group {
    display: flex;
    gap: clamp(8px, 3vw, 15px);
    margin-top: clamp(12px, 3vw, 20px);
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
  }

  .retry-button, .close-button {
    padding: clamp(6px, 2vw, 8px) clamp(12px, 4vw, 20px);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: clamp(13px, 3vw, 14px);
    transition: all 0.2s ease;
    min-width: 80px;
    flex: 1;
    max-width: 120px;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    &:active {
      transform: translateY(0);
      box-shadow: none;
    }
  }

  .retry-button {
    background: #3f4854;
    color: white;

    &:hover {
      background: #2c343d;
    }
  }

  .close-button {
    background: #f0f0f0;
    color: #333;

    &:hover {
      background: #e0e0e0;
    }
  }
`;